import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Caption } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import isEqual from 'react-fast-compare';

import { sellerSortOrders } from '@/enums';
import { QueryParams, SetActiveIdCallback } from '@/pages/Notifications/notifications.types';
import { AlertTypes, DeliveryType } from '@/redux/api/alerts';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getSortedFollowedSellersIds } from '@/redux/modules/filteredItems';
import { getFollowedSellerIds } from '@/redux/modules/followSeller';
import { fetchSellersIfNeeded } from '@/redux/modules/seller';
import { getSubscriptionIdList, getSubscriptionsById, toggleSmsAlert } from '@/redux/modules/smsAlerts';

import { AlertsRow } from './Components/AlertsRow';
import { Section } from './Components/Section';
import { TableRow } from './Components/TableRow';
import FollowedSellerRow from './FollowedSellerRow';

type Props = {
    activeId: QueryParams['section'];
    setActiveId: SetActiveIdCallback;
};

const FollowedSellersPage = ({ activeId, setActiveId }: Props) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const ids = useAppSelector(getFollowedSellerIds);

    React.useEffect(() => {
        dispatch(fetchSellersIfNeeded(ids));
    }, [dispatch, ids]);

    const sellers = useAppSelector((state) =>
        getSortedFollowedSellersIds(state, {
            sort: sellerSortOrders.NAME_LOWEST_FIRST,
        })
    );

    const alertsExist = useAppSelector(getSubscriptionIdList).length > 0;
    const isSubscribed = useAppSelector((state) => getSubscriptionsById(state, AlertTypes.FollowedAuctions))[0]
        ?.subscribed;

    const toggleAlerts = React.useCallback(
        () => dispatch(toggleSmsAlert(AlertTypes.FollowedAuctions, DeliveryType.email, !isSubscribed)),
        [dispatch, isSubscribed]
    );

    return (
        <Section
            activeId={activeId}
            component={
                <>
                    {alertsExist && (
                        <div className="border-b-0">
                            <AlertsRow alertTypeId={AlertTypes.FollowedAuctions} />
                        </div>
                    )}
                    <TableRow
                        frequency={
                            <Caption className="uppercase">
                                <FormattedMessage id="upcoming_auct" />
                            </Caption>
                        }
                        key="header"
                        type={
                            <Caption className="uppercase">
                                <FormattedMessage id="auctioneer" />
                            </Caption>
                        }
                    />
                    {sellers.map((id) => (
                        <FollowedSellerRow
                            key={`followed-seller${id}`}
                            sellerId={id}
                        />
                    ))}
                </>
            }
            count={sellers.length}
            email={
                // Must stay a direct child of TableRow's checkbox column -- its [&>label]:mr-[-12px] centering selector doesn't match through a wrapper.
                <Checkbox.Option
                    aria-label={formatMessage(
                        { id: 'notificationsPage.toggleAllNotifications' },
                        { name: 'auctioneer alerts', type: 'email' }
                    )}
                    checked={isSubscribed}
                    className="mb-0"
                    name="emailCheckbox"
                    onChange={toggleAlerts}
                />
            }
            id="auctioneers"
            setActiveId={setActiveId}
        />
    );
};

export default React.memo<Props>(FollowedSellersPage, isEqual);
